import React from 'react';
import { DEMOGRAPHICS, DEMOGRAPHIC_LABELS, levelLabel, summariseHeadcount } from '../../utils/headcount';

// Element-specific summary tables from a get_scorecard response — the same tables the
// consultant scorecard pages show (headcount, spend-by-programme, NPAT & spend, etc.).


const num = (n) => (n == null ? '—' : Number(n).toLocaleString('en-ZA'));
const money = (n) => (n == null ? '—' : `R ${Number(n).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const diffCell = (d) => {
  if (d == null) return <td>—</td>;
  return <td className={d > 0 ? 'pos' : 'neg'}>{d > 0 ? '+' : ''}{money(d)}</td>;
};

const HeadcountTable = ({ rows }) => {
  const { levels, totals, disabled } = summariseHeadcount(rows);
  return (
    <div className="cd-sc-block">
      <div className="cd-blk">Employee headcount summary</div>
      <div className="cd-table-wrap">
        <table className="cd-table">
          <thead>
            <tr><th>Level</th><th>Total</th>{DEMOGRAPHIC_LABELS.map((l) => <th key={l}>{l}</th>)}</tr>
          </thead>
          <tbody>
            {levels.map((r) => (
              <tr key={r.level}>
                <td>{levelLabel(r.level)}</td>
                <td>{num(r.total)}</td>
                {DEMOGRAPHICS.map((d) => <td key={d} className={r[d] === 0 ? 'fail' : undefined}>{r[d] ?? '—'}</td>)}
              </tr>
            ))}
            <tr className="total">
              <td>Total employees</td>
              <td>{num(totals.total)}</td>
              {DEMOGRAPHICS.map((d) => <td key={d}>{num(totals[d])}</td>)}
            </tr>
            {disabled && (
              <tr>
                <td>Of whom: {levelLabel('disabled').toLowerCase()}</td>
                <td>{num(disabled.total)}</td>
                {DEMOGRAPHICS.map((d) => <td key={d}>{disabled[d] ?? '—'}</td>)}
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const SkillsSpendTable = ({ rows }) => (
  <div className="cd-sc-block">
    <div className="cd-blk">Spend breakdown by programme</div>
    <div className="cd-table-wrap">
      <table className="cd-table">
        <thead>
          <tr><th>Programme</th>{DEMOGRAPHIC_LABELS.map((l) => <th key={l}>{l}</th>)}<th>Total</th></tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const rowTotal = row.total != null ? row.total : DEMOGRAPHICS.reduce((s, d) => s + (row[d] || 0), 0);
            return (
              <React.Fragment key={row.program}>
                <tr className="section"><td colSpan={DEMOGRAPHICS.length + 2}>{row.program}</td></tr>
                <tr>
                  <td>Actual</td>
                  {DEMOGRAPHICS.map((d) => <td key={d}>{row[d] != null ? row[d].toLocaleString() : '—'}</td>)}
                  <td>{rowTotal.toLocaleString()}</td>
                </tr>
                {row.program !== 'Absorption' && (
                  <tr>
                    <td>Target</td>
                    {DEMOGRAPHICS.map((d) => <td key={d}>{row.targets?.[d] != null ? Math.round(row.targets[d]).toLocaleString() : '—'}</td>)}
                    <td>{row.targets?.total != null ? Math.round(row.targets.total).toLocaleString() : '—'}</td>
                  </tr>
                )}
                {row.map_vals && Object.keys(row.map_vals).length > 0 && (
                  <tr>
                    <td>Max points available</td>
                    {DEMOGRAPHICS.map((d) => <td key={d}>{row.map_vals?.[d] != null ? row.map_vals[d].toFixed(2) : '—'}</td>)}
                    <td>{row.map_vals?.total != null ? row.map_vals.total.toFixed(2) : '—'}</td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  </div>
);

const PpSpendTable = ({ rows, tmps }) => (
  <div className="cd-sc-block">
    <div className="cd-blk">Spend breakdown by category</div>
    <div className="cd-table-wrap">
      <table className="cd-table">
        <thead>
          <tr><th>Category</th><th>Actual spend</th><th>Target spend</th><th>Difference</th></tr>
        </thead>
        <tbody>
          {tmps != null && (
            <tr><td>Total Measured Procurement Spend (TMPS)</td><td colSpan="3">{money(tmps)}</td></tr>
          )}
          {rows.map((r) => (
            <tr key={r.category}>
              <td>{r.category}</td>
              <td>{money(r.spend)}</td>
              <td>{money(r.target_spend)}</td>
              {diffCell(r.spend_diff)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const NpatSpendTable = ({ rows, npat, targetLabel, withLoan }) => (
  <div className="cd-sc-block">
    <div className="cd-blk">NPAT &amp; spend summary</div>
    <div className="cd-table-wrap">
      <table className="cd-table">
        <thead>
          <tr>
            <th>Category</th><th>NPAT used</th>
            <th>Target{targetLabel ? ` (${targetLabel})` : ''}</th>
            {withLoan && <th>Interest-free loan target</th>}
            <th>Actual spend</th><th>Difference</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.category}>
              <td>{r.category}</td>
              <td>{npat != null ? money(npat) : '—'}</td>
              <td>{money(r.target_spend)}</td>
              {withLoan && <td>{money(r.loan_target)}</td>}
              <td>{money(r.spend)}</td>
              {diffCell(r.spend_diff)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const nonEmpty = (v) => Array.isArray(v) && v.length > 0;

// Does this get_scorecard response carry any element-specific summary table?
export const hasSummaryTables = (data) => {
  if (!data) return false;
  const sp = data.spend_summary;
  return (
    nonEmpty(data.headcount_summary) ||
    nonEmpty(data.pp_spend_summary) ||
    nonEmpty(data.sed_spend_summary) ||
    (nonEmpty(sp) && (sp[0].program != null || sp[0].category != null))
  );
};

const SummaryTables = ({ data }) => {
  if (!data) return null;
  const blocks = [];
  const sp = data.spend_summary;

  if (nonEmpty(data.headcount_summary)) {
    blocks.push(<HeadcountTable key="hc" rows={data.headcount_summary} />);
  }
  if (nonEmpty(sp) && sp[0].program != null) {
    blocks.push(<SkillsSpendTable key="sd" rows={sp} />);
  }
  if (nonEmpty(sp) && sp[0].category != null) {
    blocks.push(<NpatSpendTable key="esd" rows={sp} npat={data.npat} targetLabel={data.spend_target_label} withLoan />);
  }
  if (nonEmpty(data.pp_spend_summary)) {
    blocks.push(<PpSpendTable key="pp" rows={data.pp_spend_summary} tmps={data.tmps} />);
  }
  if (nonEmpty(data.sed_spend_summary)) {
    blocks.push(<NpatSpendTable key="sed" rows={data.sed_spend_summary} npat={data.npat} />);
  }

  return blocks.length ? <>{blocks}</> : null;
};

export default SummaryTables;
